import { Box, Chip, Stack, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { If } from "lib/@components/switch"
import { fDateTime } from "minimals-template/components/utils/formatTime"

/**
 * The at-a-glance strip. Tripped breakers and parked users are the two numbers an operator
 * should never have to hunt for — they are the signatures of a stuck tenant (SNAG-001) and of
 * a head-of-line seat stall respectively, so both are called out in colour when non-zero.
 */
export function EstateSummary({ clients = [], generatedAt }) {
    const theme = useTheme()
    const ssoOn = clients.filter(hasSSO).length
    const scimOn = clients.filter(hasSCIM).length
    const tripped = clients.filter(isTripped).length
    const unknown = clients.filter(isUnknown).length
    const parked = clients.reduce(addParked, 0)

    return (
        <Box sx={{ my: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={`${clients.length} configured`} />
                <Chip size="small" variant="outlined" label={`${ssoOn} SSO enabled`} />
                <Chip size="small" variant="outlined" label={`${scimOn} SCIM enabled`} />
                <Chip
                    size="small"
                    variant={emphasis(tripped)}
                    color={alertColour(tripped, "error")}
                    label={`${tripped} breaker tripped`}
                    data-cy="sso-estate-tripped-count"
                />
                {/* Without this, a Redis blip turns into a confident "0 breaker tripped" for
                    tenants whose breaker was never read. Hidden entirely at zero so the strip
                    stays quiet in the normal case. */}
                <If value={unknown > 0}>
                    <Chip
                        size="small"
                        variant="filled"
                        color="warning"
                        label={`${unknown} breaker unknown`}
                        data-cy="sso-estate-unknown-count"
                    />
                </If>
                <Chip
                    size="small"
                    variant={emphasis(parked)}
                    color={alertColour(parked, "warning")}
                    label={`${parked} parked over-license`}
                    data-cy="sso-estate-parked-count"
                />
            </Stack>
            <If value={!!generatedAt}>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, mt: 1, display: "block" }}>
                    {`Snapshot taken ${snapshotTime()}`}
                </Typography>
            </If>
        </Box>
    )

    function snapshotTime() {
        return generatedAt ? fDateTime(generatedAt) : ""
    }

    function hasSSO(client) {
        return !!client.ssoEnabled
    }

    function hasSCIM(client) {
        return !!client.scimEnabled
    }

    function isTripped(client) {
        return !!client.breaker?.tripped
    }

    function isUnknown(client) {
        return !!client.breaker?.unavailable
    }

    function addParked(total, client) {
        return total + (client.parkedUsers || 0)
    }

    function emphasis(count) {
        return count ? "filled" : "outlined"
    }

    function alertColour(count, colour) {
        return count ? colour : "default"
    }
}
